import React, { useState, useCallback, useRef, useEffect } from "react";

import AddToCollectionLight from "./AddToCollectionLight";
import CompInputSentenceExplanation from "./CompInputSentenceExplanation";
import Modal from "./Modal";
import ModalProPromo from "./ModalProPromo";

export default function CompInputReader({ paragraphs, collectionsUrl, isPro, isSignedIn, signupUrl, explanationLanguageId, languageIso, languageCode }) {
  const [activeSentence, setActiveSentence] = useState(null);
  const [popoverPos, setPopoverPos] = useState(null);
  const [addToCollectionSentence, setAddToCollectionSentence] = useState(null);
  const [explainSentence, setExplainSentence] = useState(null);
  const [showProPromo, setShowProPromo] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const ttsVoiceRef = useRef(null);
  const ttsAudioRef = useRef(null);
  const readingRef = useRef(null);
  const popoverRef = useRef(null);

  const handleSentenceClick = useCallback((sentence, key, e) => {
    if (e.target.closest("button, a")) return;

    // If text is selected within this sentence, open Add to Collection with cloze
    const selection = window.getSelection();
    const selectedStr = selection.toString().trim();
    if (selectedStr && sentence.text.includes(selectedStr)) {
      if (!isSignedIn) {
        setShowSignupModal(true);
        return;
      }
      if (!isPro) {
        setShowProPromo(true);
        return;
      }
      if (collectionsUrl) {
        const clozeText = sentence.text.replace(selectedStr, "{{" + selectedStr + "}}");
        setAddToCollectionSentence({ ...sentence, text: clozeText });
        selection.removeAllRanges();
        return;
      }
    }

    setActiveSentence((prev) => {
      if (prev && prev.key === key) return null;
      return { ...sentence, key };
    });

    const span = e.currentTarget;
    const container = readingRef.current;
    if (span && container) {
      const spanRect = span.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setPopoverPos({
        top: spanRect.bottom - containerRect.top + 6,
        left: spanRect.left - containerRect.left,
      });
    }
  }, [isSignedIn, isPro, collectionsUrl]);

  const handleDismiss = useCallback(() => {
    setActiveSentence(null);
  }, []);

  // Dismiss on Escape or click outside
  useEffect(() => {
    if (!activeSentence) return;

    const handleKey = (e) => { if (e.key === "Escape") setActiveSentence(null); };

    const handleClickOutside = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        !e.target.closest(".comp-input-sentence")
      ) {
        setActiveSentence(null);
      }
    };

    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeSentence]);

  const handleAddToCollection = useCallback((sentence) => {
    if (!isSignedIn) {
      setShowSignupModal(true);
      return;
    }
    if (!isPro) {
      setShowProPromo(true);
      return;
    }
    setAddToCollectionSentence(sentence);
  }, [isSignedIn, isPro]);

  const handleExplain = useCallback((sentence) => {
    if (!isSignedIn) {
      setShowSignupModal(true);
      return;
    }
    setExplainSentence(sentence);
  }, [isSignedIn]);

  const handleListen = useCallback((sentence) => {
    // Prefer server-generated TTS audio
    if (sentence.ttsUrl) {
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
      }
      ttsAudioRef.current = new Audio(sentence.ttsUrl);
      ttsAudioRef.current.play();
      return;
    }

    // Fall back to browser TTS
    if (!window.clozemaster || !window.clozemaster.systemTtsAvailable(languageIso, languageCode)) return;

    if (!ttsVoiceRef.current) {
      const voices = window.clozemaster.getSystemTtsVoices(languageIso, languageCode);
      if (voices.length) ttsVoiceRef.current = voices[0];
    }

    if (ttsVoiceRef.current) {
      window.clozemaster.playSystemUtterance(sentence.text, ttsVoiceRef.current);
    }
  }, [languageIso, languageCode]);

  return (
    <div className="comp-input-reading" ref={readingRef}>
      {paragraphs.map((paragraph, pIdx) => (
        <p key={pIdx}>
          {paragraph.sentences.map((sentence, sIdx) => {
            const key = `${pIdx}-${sIdx}`;
            const isActive = activeSentence && activeSentence.key === key;
            return (
              <React.Fragment key={key}>
                <span
                  className={`comp-input-sentence${isActive ? " active" : ""}`}
                  onClick={(e) => handleSentenceClick(sentence, key, e)}
                >
                  {sentence.text}
                </span>
                {" "}
              </React.Fragment>
            );
          })}
        </p>
      ))}

      {activeSentence && popoverPos && (
        <div
          className="comp-input-popover"
          ref={popoverRef}
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <button className="comp-input-popover-dismiss" onClick={handleDismiss} aria-label="Dismiss">
            <span className="glyphicon glyphicon-remove"></span>
          </button>
          {/*<div className="comp-input-popover-text">{activeSentence.text}</div>*/}
          {activeSentence.translation && (
            <div className="comp-input-popover-translation">{activeSentence.translation}</div>
          )}
          <div className="comp-input-sentence-actions">
            <button
              className="btn btn-default btn-sm"
              onClick={() => handleListen(activeSentence)}
            >
              <span className="glyphicon glyphicon-volume-up"></span> Listen
            </button>
            {!!collectionsUrl && (
              <button
                className="btn btn-default btn-sm"
                onClick={() => handleAddToCollection(activeSentence)}
              >
                <span className="glyphicon glyphicon-plus"></span> Add to Collection
              </button>
            )}
            <button
              className="btn btn-default btn-sm"
              onClick={() => handleExplain(activeSentence)}
            >
              <span className="glyphicon glyphicon-comment"></span> Explain
            </button>
          </div>
        </div>
      )}

      {addToCollectionSentence && (
        <AddToCollectionLight
          text={addToCollectionSentence.text}
          translation={addToCollectionSentence.translation}
          collectionsUrl={collectionsUrl}
          onClose={() => setAddToCollectionSentence(null)}
          onSaved={() => setAddToCollectionSentence(null)}
        />
      )}

      {explainSentence && explanationLanguageId && (
        <Modal
          title="Explain"
          onHidden={() => setExplainSentence(null)}
          footer={<button className="btn btn-default" data-dismiss="modal">Close</button>}
        >
          <p style={{ marginBottom: 16, fontStyle: "italic", color: "#888" }}>
            {explainSentence.text}
          </p>
          <CompInputSentenceExplanation
            key={explainSentence.id}
            sentenceId={explainSentence.id}
            languageId={explanationLanguageId}
          />
        </Modal>
      )}

      {showSignupModal && (
        <Modal
          title="Sign Up"
          onHidden={() => setShowSignupModal(false)}
          footer={<button className="btn btn-default" data-dismiss="modal">Close</button>}
        >
          <div className="text-center" style={{ fontSize: "1.5em" }}>
            <p style={{ marginBottom: 20 }}>
              Sign up for free to get sentence explanations, save sentences to custom collections, and more.
            </p>
            <a href={signupUrl || "/signup"} className="joystix btn btn-success btn-block btn-lg">Sign up for free</a>
          </div>
        </Modal>
      )}

      {showProPromo && (
        <Modal
          title="Pro Feature"
          onHidden={() => setShowProPromo(false)}
          footer={<button className="btn btn-default" data-dismiss="modal">Close</button>}
        >
          <ModalProPromo can="add sentences to custom collections." />
        </Modal>
      )}
    </div>
  );
}
